import { useEffect } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import { useGameStore } from '../store/useGameStore';
import { useChatStore } from '../store/useChatStore';
import { useAchievementStore } from '../store/useAchievementStore';
import { useNotificationStore } from '../store/useNotificationStore';
import { useAppBadge } from '../hooks/useAppBadge';
import { useHumanTurnAlerts } from '../hooks/useHumanTurnAlerts';
import { useChatMessageAlerts } from '../hooks/useChatMessageAlerts';
import { useTurnAlertStore } from '../store/useTurnAlertStore';
import { useNavigate } from 'react-router-dom';
import { Swords } from 'lucide-react';
import { toast } from 'sonner';

export function GlobalListener() {
  const { user } = useAuthStore();
  const { fetchActiveGames } = useGameStore();
  const { subscribeToChats } = useChatStore();
  const { evaluateAchievements } = useAchievementStore();
  const { notifications, markAsRead } = useNotificationStore();
  const { pendingHumanTurnCount, pendingHumanTurnGameIds } = useTurnAlertStore();
  const navigate = useNavigate();
  
  // Watch for new notifications
  useEffect(() => {
    const unread = notifications.filter(n => !n.read);
    unread.forEach(n => {
      toast.success(n.title, {
        description: n.message,
        duration: 5000,
      });
      markAsRead(n.id);
    });
  }, [notifications, markAsRead]);
  
  // Use the app badge hook
  useAppBadge();
  useHumanTurnAlerts();
  useChatMessageAlerts();

  useEffect(() => {
    if (user) {
      console.log(`[GlobalListener] App initialized for user: ${user.nickname} (Role: ${user.role || 'user'})`);
      if (user.role === 'admin' || user.email === 'juansolisc@gmail.com') {
        console.log("[GlobalListener] Admin privileges detected.");
      } else {
        console.log("[GlobalListener] Standard beta user session.");
      }

      // Global subscriptions
      const unsubscribeGames = fetchActiveGames(user.uid);
      const unsubscribeChats = subscribeToChats(user.uid);
      
      // Initial achievements evaluation
      evaluateAchievements(user.uid);
      
      return () => {
        if (typeof unsubscribeGames === 'function') unsubscribeGames();
        unsubscribeChats();
      };
    }
  }, [user, fetchActiveGames, subscribeToChats, evaluateAchievements]);

  if (!user || pendingHumanTurnCount === 0) return null;

  return (
    <button
      type="button"
      aria-label={`${pendingHumanTurnCount} partidas con jugada pendiente`}
      onClick={() => {
        const firstGameId = pendingHumanTurnGameIds[0];
        navigate(firstGameId ? `/game/${firstGameId}` : '/');
      }}
      className="fixed right-4 top-24 z-[90] flex h-12 min-w-12 items-center justify-center rounded-2xl bg-primary-accent px-3 text-white shadow-xl shadow-primary-accent/25 border border-white/30 active:scale-95 transition-all"
    >
      <Swords className="w-5 h-5" />
      <span className="ml-1.5 text-sm font-black leading-none">{pendingHumanTurnCount}</span>
    </button>
  );
}
